// 261010 P10 F1 (S1) — «Dựng ảnh từ nhân vật»: logic THUẦN (0 antd) của dòng chữ trên thẻ + ô trong form Sửa nhân vật.
// Với người dùng, hình thái chỉ là MỘT nhân vật bình thường; liên kết chỉ hiện bằng một dòng chữ nhỏ và một ô tuỳ chọn. Không cây, không nhóm riêng.
// Hợp đồng BE (admin-service-261010 `domains/filmCharacterIdentityDomain.ts`): GET /stories/:id/characters trả CỘT THÔ
// (`identityBaseCharacterId`, `identityVariantKind`, `identityVariantDescription`, `identityMarkers`); PUT /characters/:id nhận `identity`
// (`undefined` = không đụng · `null` = bỏ liên kết · `{ baseCharacterId, kind, description, markers? }`). `markers` UI không hiện, không gửi (BE giữ cái đang có).
import type { FilmCharacter, FilmCharacterIdentityInput, IdentityVariantKind } from '../../types/film';

export const IDENTITY_KIND_LABEL: Record<IdentityVariantKind, string> = { AGE: 'Lúc khác tuổi', FORM: 'Hình thái khác' };
export const IDENTITY_NONE_LABEL = '— Không —';
export const MAX_IDENTITY_DESCRIPTION = 300; // = MAX_IDENTITY_DESCRIPTION_CHARS ở BE

type CharLike = Pick<FilmCharacter, 'id' | 'name'> & Partial<Pick<FilmCharacter, 'sourceStoryId' | 'kind' | 'identityBaseCharacterId' | 'identityVariantKind' | 'identityVariantDescription'>>;

const clean = (v: unknown): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

/** Liên kết hiện có của nhân vật (đọc cột thô BE trả). Thiếu bất kỳ phần nào của bộ ba ⇒ `null` (nhân vật thường). */
export function identityOfCharacter(c: CharLike | null | undefined): { baseCharacterId: string; kind: IdentityVariantKind; description: string } | null {
  if (!c || !c.identityBaseCharacterId) return null;
  const kind = c.identityVariantKind;
  const description = clean(c.identityVariantDescription);
  if ((kind !== 'AGE' && kind !== 'FORM') || !description) return null;
  return { baseCharacterId: c.identityBaseCharacterId, kind, description };
}

const isPerson = (c: { kind?: string | null }): boolean => (c.kind ?? 'PERSON') === 'PERSON';

/** Các hình thái đang trỏ về `selfId` (nhân vật này là GỐC của chúng). */
export function variantsOf(chars: readonly CharLike[], selfId: string): CharLike[] {
  return chars.filter((x) => x.identityBaseCharacterId === selfId);
}

/**
 * Danh sách GỐC hợp lệ cho ô «Dựng ảnh từ nhân vật»: PERSON, cùng truyện, không phải chính nó, không phải hình thái (BE không cho chuỗi), xếp theo tên.
 * Nhân vật hiện là GỐC của hình thái khác ⇒ rỗng (BE từ chối 409 IDENTITY_HAS_VARIANTS — khỏi mời người ta chọn rồi báo lỗi).
 */
export function identityBaseOptions(chars: readonly CharLike[], self: CharLike): Array<{ value: string; label: string }> {
  if (!isPerson(self) || variantsOf(chars, self.id).length) return [];
  return chars
    .filter((x) => x.id !== self.id && isPerson(x) && !x.identityBaseCharacterId && (!x.sourceStoryId || !self.sourceStoryId || x.sourceStoryId === self.sourceStoryId))
    .map((x) => ({ value: x.id, label: x.name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
}

/** Câu giải thích khi ô bị khoá vì nhân vật đang là gốc (null = ô dùng được). */
export function identityLockedNote(chars: readonly CharLike[], self: CharLike): string | null {
  const vs = variantsOf(chars, self.id);
  if (!vs.length) return null;
  return `Nhân vật này đang là gốc của ${vs.map((v) => v.name).join(', ')} — bỏ liên kết hoặc xoá chúng trước thì mới đặt được.`;
}

/** Dòng chữ nhỏ trên thẻ: «Ảnh dựng từ: <tên gốc>» + « · lúc khác tuổi» / « · hình thái khác». Nhân vật thường ⇒ `null` (thẻ giống hệt thẻ thường). */
export function identityLineOf(c: CharLike, chars: readonly CharLike[]): string | null {
  const idn = identityOfCharacter(c);
  if (!idn) return null;
  const base = chars.find((x) => x.id === idn.baseCharacterId);
  return `Ảnh dựng từ: ${base?.name || '(không còn nhân vật gốc)'} · ${idn.kind === 'AGE' ? 'lúc khác tuổi' : 'hình thái khác'}`;
}

/** Mô tả mặc định khi vừa chọn gốc: mô tả liên kết đang có → dựng từ tên (cùng công thức BE `deriveUiIdentityDescription`). */
export function defaultIdentityDescription(self: CharLike, baseName: string, kind: IdentityVariantKind): string {
  const cur = identityOfCharacter(self);
  if (cur?.description) return cur.description;
  const tail = kind === 'AGE' ? `${baseName} at a different age` : `${baseName} in a different form`;
  return `${clean(self.name)} — ${tail}`.slice(0, MAX_IDENTITY_DESCRIPTION).trim();
}

export interface IdentityDraft { baseId: string | null; kind: IdentityVariantKind; description: string }

/** Nháp khởi đầu của ô = đúng liên kết hiện có (chưa có ⇒ «Không», kind AGE). */
export function identityDraftOf(self: CharLike): IdentityDraft {
  const cur = identityOfCharacter(self);
  return cur ? { baseId: cur.baseCharacterId, kind: cur.kind, description: cur.description } : { baseId: null, kind: 'AGE', description: '' };
}

/**
 * Quyết định gửi gì khi bấm Lưu. Không đổi gì ⇒ `skip` (KHÔNG gửi field `identity`). «Không» khi đang có liên kết ⇒ `identity: null`.
 * Chọn gốc ⇒ bắt buộc có mô tả không trắng (≤ 300), nếu không `error` (form giữ nguyên, không gọi BE). Không gửi `markers`.
 */
export function buildIdentityPatch(self: CharLike, draft: IdentityDraft): { mode: 'skip' } | { mode: 'send'; identity: FilmCharacterIdentityInput | null } | { mode: 'error'; error: string } {
  const cur = identityOfCharacter(self);
  if (!draft.baseId) return cur ? { mode: 'send', identity: null } : { mode: 'skip' };
  const description = clean(draft.description);
  if (!description) return { mode: 'error', error: 'Nhập mô tả ngắn cho hình thái này (vd «Mira lúc 6 tuổi»).' };
  if (description.length > MAX_IDENTITY_DESCRIPTION) return { mode: 'error', error: `Mô tả dài quá ${MAX_IDENTITY_DESCRIPTION} ký tự.` };
  if (cur && cur.baseCharacterId === draft.baseId && cur.kind === draft.kind && cur.description === description) return { mode: 'skip' };
  return { mode: 'send', identity: { baseCharacterId: draft.baseId, kind: draft.kind, description } };
}

const CODE_TEXT: Record<string, string> = {
  IDENTITY_BASE_NOT_FOUND: 'Không tìm thấy nhân vật gốc đã chọn (có thể vừa bị xoá). Tải lại trang rồi chọn lại.',
  IDENTITY_SELF: 'Một nhân vật không thể được dựng ảnh từ chính nó (hoặc từ nhân vật đang dựng từ nó).',
  IDENTITY_CROSS_STORY: 'Nhân vật gốc thuộc truyện khác — chỉ chọn được nhân vật cùng truyện.',
  IDENTITY_NOT_PERSON: 'Chỉ nhân vật loại «người» mới dựng ảnh từ nhân vật khác được (vai lặp / đám đông thì không).',
  IDENTITY_HAS_VARIANTS: 'Nhân vật này đang là gốc của nhân vật khác — bỏ liên kết hoặc xoá những nhân vật đó trước.',
};

/**
 * Câu tiếng Việt cho lỗi BE của ô này. Mã nằm ở `response.data.code` (FilmAgentError.toJSON). Lỗi không thuộc ô này (vd WORKFLOW_RUN_ACTIVE, mạng) ⇒ `null`
 * (caller dùng đường báo lỗi chung). 400 BAD_FIELDS nói về `identity.*` ⇒ câu về mô tả.
 */
export function identityErrorText(err: any): string | null {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  if (CODE_TEXT[code]) return CODE_TEXT[code];
  const msg = Array.isArray(d?.messages) ? d.messages.join(' ') : String(d?.message || '');
  if (code === 'BAD_FIELDS' && /identity/i.test(msg)) {
    if (/description/i.test(msg)) return `Mô tả không hợp lệ — bắt buộc, không để trắng, tối đa ${MAX_IDENTITY_DESCRIPTION} ký tự.`;
    return 'Thông tin «Dựng ảnh từ nhân vật» không hợp lệ — kiểm tra lại nhân vật gốc và loại.';
  }
  return null;
}

/** Chip «cũ» trên thẻ compact: với hình thái, nói rõ ảnh dựng từ nhân vật gốc (không phải «mặt cũ»). Nhân vật thường giữ câu cũ. */
export function staleChipLabel(staleOwn: number, isVariant: boolean): string {
  return isVariant ? `${staleOwn} bộ ảnh cũ — tạo lại từ ảnh nhân vật gốc` : `${staleOwn} bộ dựng từ mặt cũ — tạo lại để khớp`;
}
